import { useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { FailureCard } from "./GoalNotice";
import { ActionButton, SecondaryAction } from "./kit";
import { GoalTitleField, GoalCategoryField, GoalHorizonField, GoalDueDateField, GoalNotesField } from "./GoalSheetFields";
import SettingsSheet from "./SettingsSheet";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";

/**
 * "Edit" on an opened goal (#1509, D405): the New goal sheet's anatomy
 * (`NewGoalSheet`, D283) prefilled from the goal, without the Actions
 * editor (milestones stay read-only, #1054). Save sends `PUT /goals/:id`
 * with only the fields that changed among `goal_title`, `target`,
 * `target_date`, `horizon`, `category` and `notes`; never `milestones` or
 * `milestone_states`. A changed title goes as both `goal_title` and `target`,
 * as creation posts it. Failures show in the sheet and keep what was typed;
 * there is no toast (D57).
 */

/** The values the sheet shows for a goal. A goal made from Plan's one sentence has only a target. */
export function editValuesOf(goal) {
  return {
    target: goal.target || goal.goal_title || "",
    target_date: goal.target_date ? String(goal.target_date).slice(0, 10) : "",
    horizon: goal.horizon || "short",
    category: goal.category || "general",
    notes: goal.notes || "",
  };
}

/** The `PUT /goals/:id` body: only what differs from the goal as saved. */
export function goalEditDiff(goal, f) {
  const was = editValuesOf(goal);
  const diff = {};
  if (f.target !== was.target) {
    diff.goal_title = f.target;
    diff.target = f.target;
  }
  if (f.target_date !== was.target_date) diff.target_date = f.target_date || null;
  if (f.horizon !== was.horizon) diff.horizon = f.horizon;
  if (f.category !== was.category) diff.category = f.category;
  if (f.notes !== was.notes) diff.notes = f.notes;
  return diff;
}

export default function EditGoalSheet({ goal, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(() => editValuesOf(goal));
  const [titleError, setTitleError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);
  const titleRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const focusErrors = useMemo(() => ({ title: titleError ? attempt : 0 }), [titleError, attempt]);
  const armFocus = useFocusFirstInvalid(titleRef, focusErrors);

  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));

  const save = async () => {
    if (saving) return;
    armFocus();
    if (!f.target.trim()) {
      setTitleError(true);
      setAttempt((n) => n + 1);
      return;
    }
    const diff = goalEditDiff(goal, f);
    if (Object.keys(diff).length === 0) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    setSaveError(false);
    try {
      await api.put(`/goals/${goal.goal_id}`, diff);
      onOpenChange(false);
      await onSaved?.();
    } catch {
      setSaveError(true);
    }
    setSaving(false);
  };

  return (
    <SettingsSheet open={open} onOpenChange={onOpenChange} title="Edit goal" data-testid="goal-edit-sheet">
      <div ref={titleRef}>
        <GoalTitleField
          value={f.target}
          onChange={(v) => {
            set({ target: v });
            if (titleError && v.trim()) setTitleError(false);
          }}
          error={titleError ? "Goal title required" : null}
        />
        {titleError && (
          <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Everything else typed is still in the sheet.</div>
        )}
      </div>
      <GoalCategoryField value={f.category} onChange={(v) => set({ category: v })} />
      <GoalHorizonField value={f.horizon} onChange={(v) => set({ horizon: v })} />
      <GoalDueDateField value={f.target_date} onChange={(v) => set({ target_date: v })} />
      <GoalNotesField value={f.notes} onChange={(v) => set({ notes: v })} />
      {saveError && <FailureCard data-testid="goal-edit-error" title="Failed to save goal" body="Everything you typed is still here. Try again." />}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <SecondaryAction variant="boxed" onClick={() => onOpenChange(false)} disabled={saving} data-testid="goal-edit-cancel-btn" style={{ width: "auto", flexShrink: 0 }}>
          Cancel
        </SecondaryAction>
        <ActionButton onClick={save} disabled={saving} data-testid="goal-edit-save-btn" style={saving ? { background: "#E2E8F0", color: "#64748B", opacity: 1 } : undefined}>
          {saving ? "Saving…" : "Save"}
        </ActionButton>
      </div>
    </SettingsSheet>
  );
}
